import { Alert, Box, Button, Chip, CircularProgress, Paper, Stack, TextField, Typography } from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';

import { integrationsApi } from '../../api/integrations.api';
import { settingsHistoryKey, type SettingsHistoryEntry } from '../../api/settings-history.api';
import { treasuryRestoreApi } from '../../api/settings-restore.api';
import { TREASURY_KEY, treasuryApi, type TreasuryField, type TreasuryProvider } from '../../api/treasury.api';
import { APP_LOCALE } from '../../utils/locale.js';

import { SelectSettingField } from './CommissionTransferControls';
import { CurrencyMapField, type CurrencyMapValue, type DiscoveredAccount } from './CurrencyMapField';
import { fillFromDiscovery, type PartnerAccount } from './discovered-accounts';
import { SettingsHistoryButton } from './SettingsHistory';
import { RestoreSaveBar } from './SettingsRestore';
import { useSectionRestore } from './useSettingsAccess';
import { SettingsSaveBar } from './SettingsSaveBar';
import { pendingChanges } from './settings-changes';

/**
 * One provider's part of a Settings → Treasury page (DEV-139): the fields of
 * one section, saved as a new version through the shared save bar, with the
 * provider's Treasury History. A different client-money or revenue account,
 * or switching to LIVE, asks for a reason (the API requires it).
 */

interface Props {
  readonly provider: TreasuryProvider;
  readonly section: TreasuryField['section'];
  /** The History panel's title. */
  readonly historyTitle: string;
  /** Lines under the provider's name. */
  readonly children?: ReactNode;
  /** Beside the provider's name, e.g. the transfer mode. */
  readonly badge?: ReactNode;
  /**
   * What "Load accounts" offers. `offer` picks the accounts a currency row may
   * choose from; `fill` fills the client-money and shared accounts from the
   * partner's answer. Absent: the provider has no API to ask (the manual bank).
   */
  readonly discovery?: {
    readonly offer: (accounts: readonly PartnerAccount[], field: TreasuryField) => readonly DiscoveredAccount[];
    readonly fill: boolean;
  };
  /**
   * The role may read Treasury but not change it (DEV-140): the values are
   * shown with no save bar, no edit controls and no "Load accounts".
   */
  readonly readOnly?: boolean;
}

/**
 * DEV-142: restore an earlier Treasury version through the save bar. The
 * changes shown are the server's dry run against what is stored (masked as
 * the reader's role requires); what is saved is the stored version, read by
 * the server. A restore that switches the transfer to LIVE must be ticked.
 */
function TreasuryRestore({ provider, version, editing, onDone, onRestored }: {
  provider: TreasuryProvider; version: number; editing: boolean; onDone: () => void; onRestored: (detail: string) => void;
}): JSX.Element | null {
  const preview = useQuery({
    queryKey: ['treasury-restore-preview', provider.provider, version, provider.version],
    queryFn: () => treasuryRestoreApi.preview(provider.provider, version, provider.version),
    retry: false,
  });
  const restore = useMutation({
    mutationFn: (reason: string) => treasuryRestoreApi.restore(provider.provider, {
      version, expectedVersion: provider.version, reason,
      ...(preview.data?.switchesToLive === true ? { confirmLiveMode: true } : {}),
    }),
    onSuccess: () => onRestored(`Version ${String(version)} restored as a new version.`),
  });
  if (editing) {
    return <Alert severity="warning" sx={{ mt: 2 }} action={<Button onClick={onDone}>Cancel restore</Button>}>
      Save or discard your edits before restoring version {String(version)}.
    </Alert>;
  }
  if (preview.isLoading) return <Box sx={{ py: 2, textAlign: 'center' }}><CircularProgress size={24} /></Box>;
  if (preview.isError || preview.data === undefined) {
    return <Alert severity="error" sx={{ mt: 2 }} action={<Button onClick={onDone}>Close</Button>}>
      {preview.error instanceof Error ? preview.error.message : `Version ${String(version)} cannot be restored.`}
    </Alert>;
  }
  const warnings = [
    'This restores the whole version: the accounts and the commission transfer together.',
    ...(preview.data.switchesToLive ? ['It switches the commission transfer to LIVE: from the next run, real money moves every day.'] : []),
    ...(preview.data.verifiesRevenueAccount ? [`A different account is checked at ${provider.vendor} before anything is stored.`] : []),
  ];
  return (
    <RestoreSaveBar
      plan={{
        label: `version ${String(version)}`,
        changes: preview.data.changes,
        warning: warnings.join(' '),
        ...(preview.data.switchesToLive ? { confirmLabel: 'I confirm: switch the commission transfer to LIVE' } : {}),
      }}
      saving={restore.isPending}
      {...(restore.isError ? { error: restore.error instanceof Error ? restore.error.message : 'The version could not be restored.' } : {})}
      onConfirm={(reason) => restore.mutate(reason)}
      onCancel={onDone}
    />
  );
}

export function TreasuryForm({ provider, section, historyTitle, children, badge, discovery, readOnly = false }: Props): JSX.Element {
  const queryClient = useQueryClient();
  // DEV-142: restore is declared per section in the shared registry.
  const restorePolicy = useSectionRestore({ group: 'treasury', id: section === 'TREASURY_ACCOUNTS' ? 'accounts' : 'commission-transfer' });
  const [restoreVersion, setRestoreVersion] = useState<number | null>(null);
  const fields = provider.fields.filter((f) => f.section === section);
  const [values, setValues] = useState<Record<string, string>>({});
  const [maps, setMaps] = useState<Record<string, CurrencyMapValue>>({});
  const [partnerAccounts, setPartnerAccounts] = useState<PartnerAccount[]>([]);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState<{ ok: boolean; detail: string } | null>(null);
  const [savedAt, setSavedAt] = useState(0);
  const changes = pendingChanges(fields, values, maps);

  const discard = (): void => {
    setValues({});
    setMaps({});
    setError('');
  };

  const save = useMutation({
    onMutate: () => {
      setError('');
      setNotice(null);
    },
    // Only what changed is sent; the API stores the latest version with these keys replaced.
    mutationFn: (reason: string | undefined) => treasuryApi.save(provider.provider, {
      expectedVersion: provider.version,
      values: Object.fromEntries(changes.map((c) => [c.key, c.after])),
      ...(reason !== undefined ? { reason } : {}),
    }),
    onSuccess: async () => {
      discard();
      setSavedAt(Date.now());
      setNotice({ ok: true, detail: 'Saved.' });
      await queryClient.invalidateQueries({ queryKey: TREASURY_KEY });
      await queryClient.invalidateQueries({ queryKey: ['integrations'] });
      await queryClient.invalidateQueries({ queryKey: settingsHistoryKey('TREASURY', provider.provider) });
    },
    onError: (err: unknown) => setError(err instanceof Error ? err.message : 'Could not save those settings.'),
  });

  const stored = (key: string): CurrencyMapValue =>
    (fields.find((f) => f.key === key)?.value as CurrencyMapValue | null) ?? {};

  const discover = useMutation({
    mutationFn: () => integrationsApi.discover(provider.provider),
    onSuccess: (result) => {
      setPartnerAccounts(result.accounts);
      setNotice({ ok: result.ok, detail: result.detail });
      if (discovery?.fill === true) {
        const keys = { masters: fields.some((f) => f.key === 'masterAccountUuids'), shared: fields.some((f) => f.key === 'sharedAccounts') };
        setMaps((current) => fillFromDiscovery(result.accounts, stored, current, keys));
      }
    },
    onError: (err: unknown) =>
      setNotice({ ok: false, detail: err instanceof Error ? err.message : `Could not list the ${provider.vendor} accounts.` }),
  });

  // An edit after a save makes "Saved." stale.
  const edit = (apply: () => void): void => {
    setNotice(null);
    apply();
  };

  const renderField = (field: TreasuryField): JSX.Element => {
    if (field.kind === 'currency-map') {
      return (
        <CurrencyMapField
          field={field}
          // An IBAN is useless without the BIC that routes to it.
          pair={field.key === 'sharedAccounts'}
          discovered={discovery === undefined ? [] : discovery.offer(partnerAccounts, field)}
          readOnly={readOnly}
          value={maps[field.key] ?? (field.value as CurrencyMapValue | null) ?? {}}
          onChange={(next) => edit(() => setMaps((current) => ({ ...current, [field.key]: next })))}
        />
      );
    }
    if (field.kind === 'select') {
      return (
        <SelectSettingField
          field={field}
          vendorId={provider.provider}
          value={values[field.key]}
          onChange={(next) => edit(() => setValues((current) => ({ ...current, [field.key]: next })))}
          readOnly={readOnly}
        />
      );
    }
    return (
      <TextField
        fullWidth
        label={`${field.label}${field.required ? '' : ' (optional)'}`}
        value={values[field.key] ?? String(field.value ?? '')}
        placeholder={field.placeholder ?? ''}
        helperText={field.help}
        InputLabelProps={{ shrink: true }}
        InputProps={{ readOnly }}
        onChange={(event) => edit(() => setValues((current) => ({ ...current, [field.key]: event.target.value })))}
        inputProps={{ name: `nvx-treasury-${provider.provider.toLowerCase()}-${field.key}`, autoComplete: 'new-password', spellCheck: false }}
      />
    );
  };

  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="h6" sx={{ flex: 1 }}>{provider.vendor}</Typography>
        {badge}
        <Chip size="small" variant="outlined" label={provider.version === 0 ? 'Nothing stored' : `Version ${String(provider.version)}`} />
        <SettingsHistoryButton subject="TREASURY" subjectId={provider.provider} title={`${historyTitle}: ${provider.vendor}`}
          keys={fields.map((f) => f.key)}
          {...(restorePolicy.canRestore && !readOnly ? { onRestore: (entry: SettingsHistoryEntry) => setRestoreVersion(Number(entry.restoreTarget)) } : {})} />
      </Stack>
      {children}
      {readOnly ? (
        <Alert severity="info" sx={{ mb: 2 }}>Read only. Your role can see these settings; an administrator changes them.</Alert>
      ) : null}
      {notice !== null ? <Alert severity={notice.ok ? 'success' : 'error'} sx={{ mb: 2 }}>{notice.detail}</Alert> : null}
      {error !== '' && changes.length === 0 ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}

      <Stack spacing={2}>
        {fields.map((field) => (
          <Box key={field.key}>
            {renderField(field)}
            {field.discover !== undefined && discovery !== undefined && !readOnly ? (
              <Stack direction="row" spacing={2} alignItems="center" sx={{ mt: 1 }}>
                <Button size="small" variant="outlined" onClick={() => discover.mutate()}
                  disabled={discover.isPending || !provider.connection.configured}>
                  {discover.isPending ? `Asking ${provider.vendor}…` : 'Load accounts'}
                </Button>
                <Typography variant="caption" color="text.secondary">
                  {partnerAccounts.length > 0
                    ? 'Choose the account in each currency row above.'
                    : `Lists the accounts ${provider.vendor} holds under our customer.`}
                </Typography>
              </Stack>
            ) : null}
          </Box>
        ))}
      </Stack>

      {restoreVersion !== null && !readOnly ? (
        <TreasuryRestore
          provider={provider}
          version={restoreVersion}
          editing={changes.length > 0}
          onDone={() => setRestoreVersion(null)}
          onRestored={(detail) => {
            setRestoreVersion(null);
            setNotice({ ok: true, detail });
            void queryClient.invalidateQueries({ queryKey: TREASURY_KEY });
            void queryClient.invalidateQueries({ queryKey: settingsHistoryKey('TREASURY', provider.provider) });
          }}
        />
      ) : null}
      {readOnly ? null : <SettingsSaveBar
        sticky
        changes={changes}
        saving={save.isPending}
        error={error}
        savedAt={savedAt}
        onDiscard={discard}
        onSave={(reason) => save.mutate(reason)}
      />}
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
        {provider.updatedAt === null
          ? 'Nothing stored yet.'
          : `Version ${String(provider.version)} saved ${new Date(provider.updatedAt).toLocaleString(APP_LOCALE)} by ${provider.updatedBy ?? 'Norvix (platform)'}.`}
      </Typography>
    </Paper>
  );
}
